import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { apiErrorMessage } from '../../../core/transport/form-api-errors';
import { Component, DestroyRef, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, finalize } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  resetPasswordForm: FormGroup;

  private readonly destroyRef = inject(DestroyRef);
  readonly loading$ = new BehaviorSubject(false);
  error = '';

  constructor() {
    const formBuilder = inject(FormBuilder);
    const activatedRoute = inject(ActivatedRoute);

    this.resetPasswordForm = formBuilder.group({
      token: [activatedRoute.snapshot.params['token'] ?? null, [Validators.required]],
      password: [null, [Validators.required, Validators.minLength(8)]],
      confirm_password: [null, [
        Validators.required,
        this.passwordMatchValidator('password')
      ]],
    });
    this.resetPasswordForm.controls['password'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetPasswordForm.controls['confirm_password'].updateValueAndValidity({ onlySelf: true });
    });
  }

  onSubmit(): void {
    if (this.loading$.value) return;
    if (this.resetPasswordForm.invalid) { this.resetPasswordForm.markAllAsTouched(); return; }
    this.error = '';
    this.loading$.next(true);

    this.authService.resetPassword(this.resetPasswordForm.value, { userVisibleErrors: false }).pipe(
      finalize(() => {
        this.loading$.next(false);
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => { void this.router.navigate(['/login']); },
      error: error => { this.error = apiErrorMessage(error, 'Could not reset the password. Please retry.'); },
    });
  }

  private passwordMatchValidator(relatedControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const relatedValue = control.parent?.get(relatedControlName)?.value;

      if (! value || ! relatedValue) {
        return null;
      }

      return value !== relatedValue ? { notMatch: true } : null;
    }
  }

}
